import { useState } from "react";
import {
  CanAccess,
  EditBase,
  Form,
  required,
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TextInput } from "@/components/admin/text-input";

import { FormToolbar } from "../layout/FormToolbar";
import type { DealActivityType } from "../types";
import { ActivityResultsSection } from "./ActivityResultsSection";
import { AttemptCapInput } from "./AttemptCapInput";
import {
  ACTIVITY_NAME_MAX_LENGTH,
  MESSAGE_TEMPLATE_MAX_LENGTH,
  toActivityTypeErrorMessage,
  validateActivityName,
  validateMessageTemplate,
} from "./activityTypeForm";
import { MessageTemplatePreview } from "./MessageTemplatePreview";

/**
 * Almikia Epic EA (TASK-113): a single catalogue activity -- direccion edits
 * its name, message template and attempt cap, and may archive/reactivate it
 * (never delete: no delete button anywhere, ticket acceptance criterion).
 * `key` (the seed/fixture handle) is never shown or editable here, per DATA
 * CONTRACT EA (1). Results and their next-step rules are edited in TASK-114
 * inside this same screen. Gated the same way as the list/create screens so
 * a non-direccion role can't reach an editable form by typing the URL
 * directly -- mirrors `DealStageConfigEdit.tsx`.
 */
export const DealActivityTypeEdit = () => {
  const notify = useNotify();
  return (
    <CanAccess
      resource="deal_activity_types"
      action="edit"
      accessDenied={<Navigate to="/" replace />}
    >
      <EditBase
        redirect="list"
        transform={(data) => ({
          ...data,
          name: data.name.trim(),
          message_template: data.message_template || null,
          max_attempts: data.max_attempts ?? null,
        })}
        mutationOptions={{
          onError: (error) =>
            notify(toActivityTypeErrorMessage(error), { type: "error" }),
        }}
      >
        <DealActivityTypeEditForm />
      </EditBase>
    </CanAccess>
  );
};

const DealActivityTypeEditForm = () => {
  const record = useRecordContext<DealActivityType>();
  const { data: otherActivityTypes } = useGetList<DealActivityType>(
    "deal_activity_types",
    { pagination: { page: 1, perPage: 100 } },
  );

  if (!record || !otherActivityTypes) return null;

  const activeNames = otherActivityTypes
    .filter(
      (activity) => activity.id !== record.id && activity.archived_at == null,
    )
    .map((activity) => activity.name);

  return (
    <div className="mt-2 max-w-lg mx-auto flex flex-col gap-4 pb-8">
      <Card>
        <CardContent>
          <Form className="space-y-4 pt-4">
            <TextInput
              source="name"
              label="resources.deal_activity_types.fields.name"
              validate={[
                required(),
                (value: string) => validateActivityName(value, activeNames),
              ]}
              maxLength={ACTIVITY_NAME_MAX_LENGTH}
              helperText={false}
            />
            <TextInput
              source="message_template"
              label="resources.deal_activity_types.fields.message_template"
              multiline
              rows={4}
              validate={validateMessageTemplate}
              maxLength={MESSAGE_TEMPLATE_MAX_LENGTH}
              helperText="resources.deal_activity_types.fields.message_template_help"
            />
            <MessageTemplatePreview source="message_template" />
            <AttemptCapInput source="max_attempts" />
            <FormToolbar />
          </Form>
        </CardContent>
      </Card>

      <ActivityResultsSection
        activityTypeId={record.id}
        activityTypes={otherActivityTypes}
      />

      <Card>
        <CardContent>
          <ActivityTypeArchiveActions record={record} />
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * "Archivar" / "Reactivar" -- mirrors `StageArchiveActions.tsx`: neither
 * action ever deletes the row, only sets/clears `archived_at`, and each asks
 * for a plain-Spanish confirmation (design-system principle: friction
 * proportional to risk) stating that past activity log entries and existing
 * tasks are kept.
 */
const ActivityTypeArchiveActions = ({
  record,
}: {
  record: DealActivityType;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isArchived = record.archived_at != null;

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await dataProvider.update("deal_activity_types", {
        id: record.id,
        data: { archived_at: isArchived ? null : new Date().toISOString() },
        previousData: record,
      });
      notify(
        isArchived
          ? "resources.deal_activity_types.archive.reactivated"
          : "resources.deal_activity_types.archive.archived",
        { type: "success" },
      );
      refresh();
      redirect("list", "deal_activity_types");
    } catch (error) {
      notify(toActivityTypeErrorMessage(error), { type: "error" });
    } finally {
      setIsSaving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div>
      <Button
        type="button"
        variant={isArchived ? "default" : "destructive"}
        className="min-h-11"
        onClick={() => setConfirmOpen(true)}
      >
        {isArchived
          ? translate("resources.deal_activity_types.action.reactivate")
          : translate("resources.deal_activity_types.action.archive")}
      </Button>
      <Confirm
        isOpen={confirmOpen}
        loading={isSaving}
        title={
          isArchived
            ? "resources.deal_activity_types.archive.confirm_reactivate_title"
            : "resources.deal_activity_types.archive.confirm_archive_title"
        }
        content={
          isArchived
            ? "resources.deal_activity_types.archive.confirm_reactivate_body"
            : "resources.deal_activity_types.archive.confirm_archive_body"
        }
        confirm={
          isArchived
            ? "resources.deal_activity_types.action.reactivate"
            : "resources.deal_activity_types.action.archive"
        }
        confirmColor={isArchived ? "primary" : "warning"}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
};
